import { looksLikeEmail } from "@shared/email-format";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Smartphone, Mail, Copy, Check, Loader2, CheckCircle2 } from "lucide-react";
import {
  type DeliveryOutcome,
  type DeliveryReason,
  deliveryFailureMessage,
} from "@shared/upload-delivery";

interface SendToPhoneDialogProps {
  clientId: string;
  targetType: "checklist_item" | "digital_asset" | "generic_section";
  targetId: string;
  targetName: string;
  fieldId?: string;
  fieldLabel?: string;
  rowId?: string;
  rowLabel?: string;
  trigger?: React.ReactNode;
  onClose?: () => void;
  /** Controlled open state — pass together with onOpenChange when the trigger must open
   *  the dialog directly (e.g. inside a clickable row where the trigger stops propagation
   *  and therefore can't rely on the default wrapper span's click bubbling up). Omit both
   *  for the default uncontrolled behaviour. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type DeliveryMethod = "sms" | "email";

function isValidPhone(value: string) {
  return /^\+?[\d\s\-().]{7,20}$/.test(value.trim());
}

// s236 (EMAIL-FORMAT-1): the one shared check (shared/email-format.ts). This dialog keeps REFUSING a bad address.
const isValidEmail = (value: string) => looksLikeEmail(value);

export function SendToPhoneDialog({
  clientId,
  targetType,
  targetId,
  targetName,
  fieldId,
  fieldLabel,
  rowId,
  rowLabel,
  trigger,
  onClose,
  open: controlledOpen,
  onOpenChange,
}: SendToPhoneDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [method, setMethod] = useState<DeliveryMethod>("sms");
  const [destination, setDestination] = useState("");
  const hasPrePopulated = useRef(false);
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  /** Set when the server reported the send did NOT happen. Drives the failure state (s168). */
  const [deliveryFailure, setDeliveryFailure] = useState<DeliveryReason | null>(null);
  const [copied, setCopied] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const displayLabel = rowLabel ?? fieldLabel ?? targetName;

  // Fetch client for phone/email pre-population
  const { data: client } = useQuery<{ phone?: string; email?: string }>({
    queryKey: ["/api/clients", clientId],
    enabled: open,
  });

  // Prefill step 1 (s168): the last recipient this client actually sent to on this method.
  // Takes precedence over the client record — the number they used last is the number they
  // most likely want again, and it is the only one that reflects an actual send.
  const { data: lastRecipient } = useQuery<{ recipient: string | null }>({
    queryKey: [`/api/clients/${clientId}/last-upload-recipient?method=${method}`],
    enabled: open,
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). Two locks, because the two actions are independent and both
  // duplicate real-world effects: a second mint orphans another live upload token, and a second
  // send is a second billed SMS to a grieving family member. Neither had a working interlock —
  // the retry button below carried no `disabled` at all, and the auto-mint's `isPending` check is
  // a READ of React state, not a lock.
  const mintFlight = useSingleFlight();
  const sendFlight = useSingleFlight();

  const createLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/upload-links", {
        clientId,
        targetType,
        targetId,
        fieldId: fieldId ?? undefined,
        rowId: rowId ?? undefined,
        expiresInHours: 24,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create upload link");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setUploadUrl(data.uploadUrl);
      setLinkToken(data.token);
    },
  });

  const sendSmsMutation = useMutation<DeliveryOutcome, Error, { token: string; phone: string }>({
    mutationFn: async ({ token, phone }) => {
      const res = await apiRequest("POST", `/api/upload-links/${token}/send-sms`, {
        phoneNumber: phone,
        label: displayLabel,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to send SMS");
      }
      return res.json();
    },
  });

  const sendEmailMutation = useMutation<DeliveryOutcome, Error, { token: string; email: string }>({
    mutationFn: async ({ token, email }) => {
      const res = await apiRequest("POST", `/api/upload-links/${token}/send-email`, {
        email,
        label: displayLabel,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to send email");
      }
      return res.json();
    },
  });

  const setOpenState = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  // Run open/close side-effects on the transition — so BOTH the uncontrolled span click
  // and a controlled parent (which flips `open` externally) generate the link on open.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      hasPrePopulated.current = false;
      setUploadUrl(null);
      setLinkToken(null);
      setSent(false);
      setDeliveryFailure(null);
      setCopied(false);
      setValidationError(null);
      setMethod("sms");
      setDestination("");
      createLinkMutation.reset();
      // One link per open (s168). The guard matters because this effect also runs when the
      // component is REMOUNTED while the parent still says `open` — which is what minted a
      // second token per dialog and made the dialog appear to resurrect after Confirm & Save.
      // The remount itself is fixed at the parent (ExpandableSectionLayout hoists
      // CaptureButtons); this is the belt to that braces.
      // s170: was `if (!createLinkMutation.isPending)` — an `isPending` READ, which lags a render
      // and so cannot stop two mints inside one tick. The lock is synchronous.
      mintFlight.runQuiet(() => createLinkMutation.mutateAsync());
    } else if (!open && prevOpenRef.current) {
      onClose?.();
    }
    prevOpenRef.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill precedence (s168): last recipient for this client+method → client record → empty.
  // Runs once per open, after BOTH sources have had a chance to load, so the weaker source
  // cannot win a race against the stronger one.
  useEffect(() => {
    if (!open || hasPrePopulated.current) return;
    if (lastRecipient === undefined || client === undefined) return; // still loading
    hasPrePopulated.current = true;
    const fromRecord = method === "sms" ? (client?.phone ?? "") : (client?.email ?? "");
    setDestination(lastRecipient?.recipient || fromRecord || "");
  }, [client, lastRecipient, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching tabs re-runs the same precedence for the new method. `hasPrePopulated` is
  // reset so the last-recipient query for the newly-selected method can fill in when it
  // resolves; the field stays freely editable either way.
  const handleMethodChange = (m: DeliveryMethod) => {
    setMethod(m);
    setValidationError(null);
    setDeliveryFailure(null);
    hasPrePopulated.current = false;
    setDestination(m === "sms" ? (client?.phone ?? "") : (client?.email ?? ""));
  };

  const handleSend = async () => {
    setValidationError(null);
    if (!uploadUrl) return;

    if (method === "sms") {
      if (!isValidPhone(destination)) {
        setValidationError("Please enter a valid phone number.");
        return;
      }
      // Use server-side Twilio SMS when a token is available
      if (linkToken) {
        try {
          const outcome = await sendFlight.run(() =>
            sendSmsMutation.mutateAsync({ token: linkToken, phone: destination.trim() }),
          );
          // s170: `undefined` means the lock rejected this as a duplicate fire. A skipped send
          // must do NOTHING — in particular it must not fall through to the sms: fallback below,
          // which would open the device's SMS app for a message that is already being sent.
          if (!outcome) return;
          // s168: the server's answer is now read. Previously `setSent(true)` ran
          // unconditionally below, so an unconfigured provider rendered "Link sent!".
          if (outcome.sent) setSent(true);
          else setDeliveryFailure(outcome.reason);
          return;
        } catch {
          // Fall back to native sms: link on failure
          const body = encodeURIComponent(
            `Please use this link to upload a document for "${displayLabel}":\n\n${uploadUrl}\n\nThis link expires in 24 hours.`
          );
          window.open(`sms:${destination.trim()}?body=${body}`);
        }
      } else {
        const body = encodeURIComponent(
          `Please use this link to upload a document for "${displayLabel}":\n\n${uploadUrl}\n\nThis link expires in 24 hours.`
        );
        window.open(`sms:${destination.trim()}?body=${body}`);
      }
    } else {
      if (!isValidEmail(destination)) {
        setValidationError("Please enter a valid email address.");
        return;
      }
      // s168 C4: a real server-side send with the same honesty contract as SMS. This
      // replaces the mailto: handoff, which opened a mail client and assumed the best —
      // it could not report delivery at all.
      if (linkToken) {
        try {
          const outcome = await sendFlight.run(() =>
            sendEmailMutation.mutateAsync({ token: linkToken, email: destination.trim() }),
          );
          if (!outcome) return; // duplicate fire — see the SMS branch
          if (outcome.sent) setSent(true);
          else setDeliveryFailure(outcome.reason);
        } catch {
          setDeliveryFailure("failed");
        }
        return;
      }
      // No token (link generation failed): fall back to the device's mail client, the same
      // shape as the SMS branch's no-token fallback.
      const subject = encodeURIComponent(`Upload Document: ${displayLabel}`);
      const body = encodeURIComponent(
        `Please use this link to upload a document for "${displayLabel}":\n\n${uploadUrl}\n\nThis link expires in 24 hours.`
      );
      window.open(`mailto:${destination.trim()}?subject=${subject}&body=${body}`);
    }
    setSent(true);
  };

  const handleCopy = async () => {
    if (!uploadUrl) return;
    await navigator.clipboard.writeText(uploadUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const dialogTitle = rowLabel
    ? `Send to Phone — ${rowLabel}`
    : fieldLabel
    ? `Send to Phone — ${fieldLabel}`
    : "Send to Phone";

  return (
    <Dialog open={open} onOpenChange={setOpenState}>
      {/* Trigger. Controlled mode: the parent owns the trigger's onClick (so it can
          stopPropagation AND open in a single handler); render it bare. Uncontrolled:
          wrap in a span whose click opens the dialog by bubbling up to it. */}
      {isControlled ? (
        trigger
      ) : (
        <span
          role="button"
          tabIndex={0}
          onClick={() => setOpenState(true)}
          onKeyDown={(e) => e.key === "Enter" && setOpenState(true)}
          style={{ display: "contents" }}
        >
          {trigger || (
            <Button variant="outline" size="sm" data-testid="button-send-to-phone">
              <Smartphone className="h-4 w-4 mr-2" />
              Send to Phone
            </Button>
          )}
        </span>
      )}

      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ color: "#2C3E50" }}>{dialogTitle}</DialogTitle>
        </DialogHeader>

        {/* Loading: generating link */}
        {createLinkMutation.isPending && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Generating link…</p>
          </div>
        )}

        {/* Error: failed to generate link */}
        {createLinkMutation.isError && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-destructive">{createLinkMutation.error?.message}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => mintFlight.runQuiet(() => createLinkMutation.mutateAsync())}
              disabled={mintFlight.inFlight}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Success: link sent */}
        {sent && uploadUrl && (
          <div className="py-4 flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-green-50 p-3">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-sm text-foreground">
              Link sent! Open it on your phone to upload your document
              {displayLabel ? <> for <strong>{displayLabel}</strong></> : ""}.
            </p>
            <p className="text-xs text-muted-foreground">
              This link expires in 24 hours.
            </p>
            <div className="w-full space-y-2 pt-1">
              <Button
                className="w-full"
                style={{ backgroundColor: "#1ABC9C", color: "#fff" }}
                onClick={() => setOpenState(false)}
                data-testid="button-done"
              >
                Done
              </Button>
              <button
                type="button"
                className="w-full text-xs text-muted-foreground hover:text-foreground py-1 flex items-center justify-center gap-1.5"
                onClick={handleCopy}
                data-testid="button-copy-link"
              >
                {copied ? (
                  <><Check className="h-3.5 w-3.5 text-green-600" /> Link copied!</>
                ) : (
                  <><Copy className="h-3.5 w-3.5" /> Or copy link</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Delivery failure (s168): the send did not happen and we say so, rather than
            rendering the success state over a message that was never delivered. */}
        {deliveryFailure && uploadUrl && !sent && (
          <div className="space-y-3 py-2" data-testid="delivery-failure">
            <p className="text-sm text-foreground">
              {deliveryFailureMessage(deliveryFailure, method)}
            </p>
            <button
              type="button"
              className="w-full flex items-center justify-center gap-1.5 rounded-md border border-border py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              onClick={handleCopy}
              data-testid="button-copy-link-failure"
            >
              {copied ? (
                <><Check className="h-4 w-4 text-green-600" /> Link copied!</>
              ) : (
                <><Copy className="h-4 w-4" /> Copy link</>
              )}
            </button>
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
              onClick={() => setDeliveryFailure(null)}
              data-testid="button-try-again"
            >
              Try a different {method === "sms" ? "number" : "address"}
            </button>
          </div>
        )}

        {/* Main form */}
        {!createLinkMutation.isPending && !createLinkMutation.isError && uploadUrl && !sent && !deliveryFailure && (
          <div className="space-y-4 pt-1">
            {/* Delivery method toggle */}
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors"
                style={
                  method === "sms"
                    ? { backgroundColor: "#2C3E50", color: "#fff" }
                    : { backgroundColor: "transparent", color: "#718096" }
                }
                onClick={() => handleMethodChange("sms")}
                data-testid="tab-sms"
              >
                <Smartphone className="h-4 w-4" />
                SMS / Text
              </button>
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors"
                style={
                  method === "email"
                    ? { backgroundColor: "#2C3E50", color: "#fff" }
                    : { backgroundColor: "transparent", color: "#718096" }
                }
                onClick={() => handleMethodChange("email")}
                data-testid="tab-email"
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
            </div>

            {/* Input */}
            <div className="space-y-1.5">
              <Input
                type={method === "email" ? "email" : "tel"}
                placeholder={method === "sms" ? "Mobile number" : "Email address"}
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value);
                  setValidationError(null);
                }}
                className={validationError ? "border-destructive" : ""}
                data-testid="input-destination"
                autoFocus
              />
              {validationError && (
                <p className="text-xs text-destructive">{validationError}</p>
              )}
            </div>

            {/* Send button */}
            <Button
              className="w-full font-semibold"
              style={{ backgroundColor: "#1ABC9C", color: "#fff" }}
              onClick={handleSend}
              disabled={!destination.trim() || sendFlight.inFlight || sendSmsMutation.isPending || sendEmailMutation.isPending}
              data-testid="button-send-link"
            >
              {/* The destination rides in the action itself (s168) — the planner confirms
                  WHERE it is going at the moment they commit, not by re-reading the field. */}
              {(sendSmsMutation.isPending || sendEmailMutation.isPending)
                ? "Sending…"
                : `${method === "sms" ? "Send Text" : "Send Email"}${destination.trim() ? ` to ${destination.trim()}` : ""}`}
            </Button>

            {method === "sms" && !linkToken && (
              <p className="text-xs text-muted-foreground text-center -mt-1">
                Opens your device's SMS app with the link pre-filled.
                On desktop, use <strong>Copy link</strong> instead.
              </p>
            )}

            {/* Copy link — primary fallback on desktop */}
            <button
              type="button"
              className="w-full flex items-center justify-center gap-1.5 rounded-md border border-border py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              onClick={handleCopy}
              data-testid="button-copy-link"
            >
              {copied ? (
                <><Check className="h-4 w-4 text-green-600" /> Link copied!</>
              ) : (
                <><Copy className="h-4 w-4" /> Copy link</>
              )}
            </button>

            {/* s168: "single use" was false. generic_section links are deliberately exempt
                from the used_at gate (extraction-routes.ts) so a phone can upload several
                documents from one link — which is why the upload page offers "Upload
                Another". The behaviour is the design; the copy was wrong. */}
            <p className="text-xs text-muted-foreground text-center">
              Link expires in 24 hours
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
